/**
 * §22 Sprint A — Adjuntos por contacto/proyecto. Reusa el storage del backend; la
 * descarga es autenticada (no ruta pública) por aislamiento cross-tenant.
 * Doble enforcement: el botón de subir/eliminar se oculta sin `canEdit`, y el
 * backend igual valida (403). Tipos/tamaño se validan server-side.
 */
import { useEffect, useRef, useState } from 'react';
import { api, apiUpload, apiDownload } from '../lib/api';

const ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,' +
  'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const fmtSize = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('es-MX') : '—');

type Attachment = {
  id: string; fileName: string; mimeType: string; sizeBytes: number;
  createdAt: string; downloadUrl: string;
};

export function AttachmentsPanel({
  owner, ownerId, canEdit,
}: { owner: 'contact' | 'project'; ownerId: string; canEdit: boolean }) {
  const base = owner === 'contact' ? `/contacts/${ownerId}/attachments` : `/projects/${ownerId}/attachments`;
  const [items, setItems] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    api<Attachment[]>(base)
      .then(setItems)
      .catch((e: any) => setErr(e?.body?.message || e?.message || 'No se pudieron cargar los adjuntos'));
  };
  useEffect(load, [base]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      await apiUpload(base, file);
      load();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo subir el archivo');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onDelete = async (id: string) => {
    setErr(null);
    try {
      await api(`/attachments/${id}`, { method: 'DELETE' });
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo eliminar');
    }
  };

  const onDownload = async (a: Attachment) => {
    try { await apiDownload(a.downloadUrl.replace(/^\/api\/v1/, ''), a.fileName); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo descargar'); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h3 className="font-serif text-base tracking-tight text-slate-900">Adjuntos</h3>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{items.length}</span>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onPick} disabled={busy} />
              <button
                className="rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
              >
                {busy ? 'Subiendo…' : 'Subir archivo'}
              </button>
            </>
          )}
        </div>
      </div>

      {err && <p className="px-5 py-2 text-xs text-rose-600">{err}</p>}

      {items.length === 0
        ? <p className="px-5 py-4 text-sm text-slate-400">Sin adjuntos.</p>
        : (
          <div className="divide-y divide-slate-100">
            {items.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-5 py-3 text-sm">
                <button className="truncate text-left text-slate-700 hover:underline" onClick={() => onDownload(a)} title={a.fileName}>
                  📎 {a.fileName}
                </button>
                <span className="flex shrink-0 items-center gap-3">
                  <span className="text-[11px] text-slate-400">{fmtSize(a.sizeBytes)} · {fmtDate(a.createdAt)}</span>
                  {canEdit && (
                    <button className="text-[11px] text-rose-500 hover:underline" onClick={() => onDelete(a.id)}>Eliminar</button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      <p className="px-5 py-2 text-[11px] text-slate-400">Máx 10MB. Imágenes, PDF, Office, CSV/TXT.</p>
    </div>
  );
}
